import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import moment from 'moment';
import { formatListValue } from '@/shared/components/FormattedListValue';
const messages = defineMessages({
    AddFormatParts: { id: 'AddFormatParts', defaultMessage: 'Add format parts' },
    Yy: { id: '[Yy]', defaultMessage: '[Yy]' },
    Yyyy: { id: '[Yyyy]', defaultMessage: '[Yyyy]' },
    Mm: { id: '[Mm]', defaultMessage: '[Mm]' },
    Counter: { id: '[Counter:Nx]', defaultMessage: '[Counter:Nx]' },
    Text: { id: 'Text', defaultMessage: 'Text' },
    CounterLabel: { id: 'CounterLabel', defaultMessage: 'Counter #' },
    YearTitle: { id: 'Year', defaultMessage: 'Year' },
    MonthTitle: { id: 'MonthTitle', defaultMessage: 'Month of the invoice date' },
    InvoiceCounterTitle: { id: 'InvoiceCounterTitle', defaultMessage: 'Invoice counter' },
    FreeTextTitle: { id: 'FreeTextTitle', defaultMessage: 'Free text' },
    YearFormatInfo: {
        id: 'YearFormatInfo',
        defaultMessage: '[yy] and [yyyy] currently use {year}. Change the setting above to change this.',
    },
    Example: { id: 'Example', defaultMessage: 'Example' },
    FirstNumber: { id: 'FirstNumber', defaultMessage: 'FirstNumber' },
    Save: { id: 'Save', defaultMessage: 'Save' },
    Clear: { id: 'clear', defaultMessage: 'clear' },
    Reset: { id: 'Reset', defaultMessage: 'Reset' },
    Cancel: { id: 'Cancel', defaultMessage: 'Cancel' },
    Warning: {
        id: 'Warning',
        defaultMessage: 'Warning: changing the number format only affects newly created invoices. Invoices that were already finalized will not be modified.',
    },
});
const parseNumberFormat = (format) => {
    const elements = [];
    if (!format)
        return elements;
    let part = '';
    let inBlock = false;
    for (let i = 0; i < format.length; i++) {
        const c = format[i];
        if (!inBlock && c === '[') {
            inBlock = true;
            if (part !== '') {
                elements.push({ id: 'Text', t: part });
                part = '';
            }
        }
        else if (inBlock && c === ']') {
            inBlock = false;
            part = part.toLowerCase();
            switch (part) {
                case 'yyyy':
                    elements.push({ id: 'Yyyy', n: 0 });
                    break;
                case 'yy':
                    elements.push({ id: 'Yy', n: 0 });
                    break;
                case 'mm':
                    elements.push({ id: 'Mm', n: 0 });
                    break;
                default: {
                    const n = parseInt(part.replace('counter:n', '').trim());
                    if (Number.isInteger(n)) {
                        elements.push({ id: 'Counter', n });
                    }
                    break;
                }
            }
            part = '';
        }
        else {
            part += c;
        }
    }
    if (part !== '') {
        elements.push({ id: 'Text', t: part });
    }
    return elements;
};
const generateDateParts = (date) => ({
    Yyyy: date.getFullYear(),
    Yy: String(date.getFullYear()).slice(-2),
    Mm: String(date.getMonth() + 1).padStart(2, '0'),
});
const randomCounter = Math.round(Math.random() * 500);
const generateExample = (input, startDate, yearFormat, counter) => {
    if (!input)
        return null;
    const elements = typeof input === 'string' ? parseNumberFormat(input) : Array.isArray(input) ? input : [];
    if (!elements.length)
        return null;
    const exampleDate = startDate ? moment.utc(startDate).toDate() : moment.utc().toDate();
    if (startDate && yearFormat === 3) {
        exampleDate.setFullYear(exampleDate.getFullYear() + 1);
    }
    const exampleCounter = (counter && parseInt(String(counter))) || randomCounter;
    const dateParts = generateDateParts(exampleDate);
    const examples = Object.assign(Object.assign({}, dateParts), { Counter: exampleCounter });
    return elements.reduce((result, el) => {
        if (el.id === 'Text')
            return result + el.t;
        if (el.id === 'Counter')
            return result + String(examples[el.id]).padStart(el.n, '0');
        if (el.id in examples)
            return result + examples[el.id];
        return result;
    }, '');
};
const serializeElements = (elements) => elements.reduce((result, el) => {
    if (el.id === 'Text')
        return result + (el.t || '');
    if (el.id === 'Counter')
        return result + `[Counter:N${el.n || 0}]`;
    return result + `[${el.id}]`;
}, '');
const EditableNumberFormat = ({ value, onSubmit, startDate, firstNumber, yearFormat, showWarning = false, readOnly = false, }) => {
    const intl = useIntl();
    const [editMode, setEditMode] = useState(false);
    const [elements, setElements] = useState([]);
    const [saving, setSaving] = useState(false);
    const hasCounter = elements.some(el => el.id === 'Counter');
    const startEdit = () => {
        if (readOnly)
            return;
        setElements(parseNumberFormat(value || ''));
        setEditMode(true);
    };
    const addPart = (type) => {
        if (type === 'Counter' && hasCounter)
            return;
        let newEl;
        switch (type) {
            case 'Text':
                newEl = { id: 'Text', t: '' };
                break;
            case 'Counter':
                newEl = { id: 'Counter', n: 0 };
                break;
            default:
                newEl = { id: type, n: 0 };
                break;
        }
        setElements([...elements, newEl]);
    };
    const removePart = (index) => {
        setElements(elements.filter((_, i) => i !== index));
    };
    const updatePart = (index, updates) => {
        setElements(elements.map((el, i) => (i === index ? Object.assign(Object.assign({}, el), updates) : el)));
    };
    const movePart = (fromIndex, toIndex) => {
        const updated = [...elements];
        const [moved] = updated.splice(fromIndex, 1);
        updated.splice(toIndex, 0, moved);
        setElements(updated);
    };
    const handleSave = async () => {
        if (saving || !hasCounter)
            return;
        setSaving(true);
        try {
            const result = serializeElements(elements);
            await onSubmit(result);
            setEditMode(false);
        }
        catch (_a) {
            // stay in edit mode
        }
        finally {
            setSaving(false);
        }
    };
    const handleClear = () => setElements([]);
    const handleReset = () => setElements(parseNumberFormat(value || ''));
    const handleCancel = () => setEditMode(false);
    const yearFormatLabel = formatListValue({
        list: 'numberFormatYear',
        id: yearFormat !== null && yearFormat !== void 0 ? yearFormat : 0,
        fallback: '',
        intl,
    });
    if (!editMode) {
        return (_jsxs("span", { children: [_jsx("span", Object.assign({ className: "bold clickable", style: { width: 200, display: 'inline-block' }, onClick: startEdit }, { children: value || '' })), _jsx("span", Object.assign({ className: "margin20L" }, { children: intl.formatMessage(messages.Example) })), ":\u00A0", generateExample(value || '', startDate, yearFormat)] }));
    }
    return (_jsxs("div", Object.assign({ className: "numberFormatForm", id: "invoiceNumberInfo" }, { children: [_jsx("label", Object.assign({ className: "margin10R form-control-static" }, { children: intl.formatMessage(messages.AddFormatParts) })), _jsxs("div", Object.assign({ className: "btn-group", role: "group" }, { children: [_jsx("button", Object.assign({ className: "btn btn-default", onClick: () => addPart('Yy'), title: intl.formatMessage(messages.YearTitle) }, { children: intl.formatMessage(messages.Yy) })), _jsx("button", Object.assign({ className: "btn btn-default", onClick: () => addPart('Yyyy'), title: intl.formatMessage(messages.YearTitle) }, { children: intl.formatMessage(messages.Yyyy) })), _jsx("button", Object.assign({ className: "btn btn-default", onClick: () => addPart('Mm'), title: intl.formatMessage(messages.MonthTitle) }, { children: intl.formatMessage(messages.Mm) })), !hasCounter && (_jsx("button", Object.assign({ className: "btn btn-default", onClick: () => addPart('Counter'), title: intl.formatMessage(messages.InvoiceCounterTitle) }, { children: intl.formatMessage(messages.Counter) }))), _jsx("button", Object.assign({ className: "btn btn-default", onClick: () => addPart('Text'), title: intl.formatMessage(messages.FreeTextTitle) }, { children: intl.formatMessage(messages.Text) }))] })), _jsx("div", { children: intl.formatMessage(messages.YearFormatInfo, { year: yearFormatLabel }) }), _jsx("br", {}), _jsx("div", Object.assign({ className: "dropzone" }, { children: elements.map((el, index) => {
                    var _a;
                    return (_jsxs("div", Object.assign({ className: "bold part", style: { display: 'inline-block', margin: 2 } }, { children: [el.id === 'Yy' && _jsx("span", Object.assign({ className: "bold margin5R" }, { children: "[Yy]" })), el.id === 'Yyyy' && _jsx("span", Object.assign({ className: "bold margin5R" }, { children: "[Yyyy]" })), el.id === 'Mm' && _jsx("span", Object.assign({ className: "bold margin5R" }, { children: "[Mm]" })), el.id === 'Counter' && (_jsx("span", Object.assign({ className: "bold margin5R" }, { children: intl.formatMessage(messages.CounterLabel) }))), _jsxs("span", Object.assign({ style: { display: 'inline-block', margin: '0 4px' } }, { children: [el.id === 'Text' && (_jsx("input", { type: "text", className: "input-sm", size: 10, value: el.t || '', onChange: e => updatePart(index, { t: e.target.value }) })), el.id === 'Counter' && (_jsx("input", { type: "number", className: "input-sm", min: 0, max: 9, style: { minWidth: 45 }, value: (_a = el.n) !== null && _a !== void 0 ? _a : 0, onChange: e => updatePart(index, { n: parseInt(e.target.value) || 0 }) }))] })), index > 0 && (_jsx("a", Object.assign({ className: "clickable margin5R", onClick: () => movePart(index, index - 1) }, { children: _jsx("i", { className: "fa fa-arrow-left" }) }))), index < elements.length - 1 && (_jsx("a", Object.assign({ className: "clickable margin5R", onClick: () => movePart(index, index + 1) }, { children: _jsx("i", { className: "fa fa-arrow-right" }) }))), _jsx("a", Object.assign({ className: "clickable", onClick: () => removePart(index) }, { children: _jsx("i", { className: "fa fa-times" }) }))] }), index));
                }) })), _jsxs("div", { children: [_jsx("span", { children: intl.formatMessage(messages.Example) }), _jsx("span", { children: ":\u00A0" }), _jsx("span", Object.assign({ className: "bold" }, { children: generateExample(elements, startDate, yearFormat) }))] }), firstNumber != null && (_jsxs("div", { children: [_jsx("span", { children: intl.formatMessage(messages.FirstNumber) }), _jsx("span", { children: ":\u00A0" }), _jsx("span", Object.assign({ className: "bold" }, { children: generateExample(elements, startDate, yearFormat, firstNumber) }))] })), _jsx("br", {}), showWarning && (_jsxs(_Fragment, { children: [_jsx("div", Object.assign({ className: "bold" }, { children: intl.formatMessage(messages.Warning) })), _jsx("br", {})] })), _jsxs("div", Object.assign({ className: "btn-group", role: "group" }, { children: [_jsxs("button", Object.assign({ className: "btn btn-inverse", onClick: handleSave, disabled: saving || !hasCounter }, { children: [saving && _jsx("i", { className: "fa fa-spinner fa-spin margin5R" }), intl.formatMessage(messages.Save)] })), _jsx("button", Object.assign({ className: "btn btn-default", onClick: handleClear }, { children: intl.formatMessage(messages.Clear) })), _jsx("button", Object.assign({ className: "btn btn-default", onClick: handleReset }, { children: intl.formatMessage(messages.Reset) })), _jsx("button", Object.assign({ className: "btn btn-default", onClick: handleCancel }, { children: intl.formatMessage(messages.Cancel) }))] }))] })));
};
export default EditableNumberFormat;
